import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Code,
  EllipsisVertical,
  FileSearch,
  LayoutDashboard,
  LoaderCircle,
  Shield,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { cn } from '@/_shared/lib/utils';
import type { SSEMessage } from '@/_shared/types';

import { ChatSharePopover } from '../components/ChatSharePopover';
import { MessageInput } from '../components/MessageInput';
import { MessageList } from '../components/MessageList';
import { useSSE } from '../hooks/useSSE';
import { filterOutTurn, sliceIntoTurns } from '../lib/turn-slicing';
import { findUserMessageInTurn } from '../lib/turn-utils';

/**
 * Extract a user-facing message from a thrown error.
 *
 * oRPC's client deserializes error responses into ORPCError instances whose
 * `.message` is already the server-provided string (see StandardRPCLinkCodec
 * + isORPCErrorJson). Since ORPCError extends Error, we can treat all Error
 * subclasses uniformly here and just surface `.message`.
 */
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}

interface Category {
  label: string;
  icon: typeof Code;
  examples: { label: string; prompt: string }[];
}

const CATEGORIES: Category[] = [
  {
    label: 'Instrument',
    icon: Code,
    examples: [
      {
        label: 'Add a new event to my tracking',
        prompt:
          "I want to track a user action that isn't instrumented yet. Look at my source code, help me figure out where to add the tracking call, and create a pull request with the new event and its properties.",
      },
      {
        label: 'Add a property to an existing event',
        prompt:
          'I need an additional property on one of my tracked events. Find the event in my source code, figure out what data is available at that point, and create a pull request adding the new property.',
      },
      {
        label: 'Add tracking to a new feature',
        prompt:
          "I'm building a new feature and need analytics instrumentation for it. Look at my source code, identify the key user actions in the feature, and create a pull request adding tracking events with the right properties.",
      },
      {
        label: 'Instrument my app from scratch',
        prompt:
          'My codebase has little or no event tracking. Scan my source code, identify the most important user actions to track, and create a pull request adding a complete instrumentation layer with well-named events and properties.',
      },
    ],
  },
  {
    label: 'Fix',
    icon: ShieldCheck,
    examples: [
      {
        label: 'Find columns with unexpected nulls and trace to source',
        prompt:
          'Scan my warehouse tables for columns with suspicious null rates. For each one, trace back to the source code to determine whether it is a missing property, a conditional bug, or a recent regression.',
      },
      {
        label: 'Diagnose events landing in rudder_discards',
        prompt:
          'Check my rudder_discards table, identify why events are being discarded — type mismatches, schema violations, or malformed payloads — and suggest a fix via transformation or code change.',
      },
      {
        label: 'Detect schema drift and create a fix PR',
        prompt:
          'Compare event schemas in my source code against the actual warehouse table columns. For any drift — missing columns, type mismatches, renamed properties — create a pull request to fix the source code.',
      },
      {
        label: 'Fix a data issue with a RudderStack transformation',
        prompt:
          'Find a data quality problem in my warehouse — like malformed values, inconsistent formats, or missing fields — and write a RudderStack user transformation to fix it without requiring a code deploy.',
      },
    ],
  },
  {
    label: 'Build',
    icon: LayoutDashboard,
    examples: [
      {
        label: 'Build a dashboard tracking event volume trends',
        prompt:
          'Explore my warehouse schema to find event tables and timestamps. Build a dashboard with: a metric for total events, a line chart of daily event counts, a bar chart breaking down events by source or type, and a table of top events by volume. Include a time_range parameter and discover actual values via DISTINCT queries for filter dropdowns. Preview each panel before saving.',
      },
      {
        label: 'Build a conversion funnel dashboard',
        prompt:
          'Explore my warehouse to identify user journey steps (page views, signups, key actions, purchases). Build a dashboard with: a funnel panel showing conversion rates between steps, metrics for overall conversion rate and total users entering, and a line chart of funnel trends over time. Include a time_range parameter. Discover event names via DISTINCT. Preview each panel before saving.',
      },
      {
        label: 'Build a data quality monitoring dashboard',
        prompt:
          'Explore my warehouse to find null columns, rudder_discards events, and schema issues. Build a dashboard with: metrics for null rate and discard count, a bar chart of null rates by table/column, a table of recent discards with error reasons, and a line chart of quality trends over time. Include a time_range parameter. Preview each panel before saving.',
      },
      {
        label: 'Build a feature engagement dashboard',
        prompt:
          'Explore my warehouse to find track events and map them to features. Build a dashboard with: a bar chart ranking features by volume, a line chart of engagement trends for top features, metrics for unique users and most-used feature, and a per-feature breakdown table. Include a time_range parameter and discover values via DISTINCT for filters. Preview each panel before saving.',
      },
    ],
  },
  {
    label: 'Understand',
    icon: FileSearch,
    examples: [
      {
        label: 'What data do I have in my warehouse?',
        prompt:
          "Explore my warehouse tables and give me a summary — which events are tracked, data volumes, key properties. Help me understand what I'm working with.",
      },
      {
        label: 'Trace an event end-to-end: code to warehouse',
        prompt:
          'Pick a specific event and trace its journey from source code through RudderStack to the warehouse table. Show each step.',
      },
      {
        label: 'Spot anomalies in event volume over the past 30 days',
        prompt:
          'Chart event volumes from my warehouse over the last 30 days. Flag unusual spikes, drops, or trends and correlate with recent deployments.',
      },
      {
        label: 'Break down an event by its property values',
        prompt:
          'Pick a key event and show how its property values are distributed — common values, outliers, and trends over time.',
      },
    ],
  },
];

export function Chat(): React.ReactElement {
  const { id: sessionId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { slug, wp } = useWorkspace();
  const orpc = useOrpc();

  const routerTurnId = (location.state as { pendingTurnId?: string } | null)?.pendingTurnId ?? null;

  const [sending, setSending] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Check if RudderStack settings are configured
  const { data: settingsData, isLoading: settingsLoading } = useQuery(orpc.settings.get.queryOptions({ input: {} }));

  // Promoted skills — used to gate the slash menu and populate chip titles
  const { data: contextHubData } = useQuery(orpc.contextHub.list.queryOptions({ input: {} }));
  const promotedSkills = (contextHubData?.items ?? []).filter(
    (item) => item.kind === 'note' && item.adherence === 'skill' && item.skillEnabled,
  );
  const hasPromotedSkills = promotedSkills.length > 0;

  const s = settingsData?.settings;
  const hasRudderstack = s ? !!(s.rudderstackWorkspaceId && s.rudderstackPat) : null;
  const canBootstrapSession = Boolean(sessionId) && !settingsLoading && hasRudderstack !== false;
  const activeSessionId = canBootstrapSession ? (sessionId ?? null) : null;

  // Fetch current session details
  const { data: sessionData } = useQuery(
    orpc.chat.get.queryOptions({
      input: activeSessionId ? { id: activeSessionId } : skipToken,
    }),
  );
  // Create session mutation
  const createMutation = useMutation(
    orpc.chat.create.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        return data;
      },
    }),
  );

  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);

  // Send message mutation
  const sendMessageMutation = useMutation(
    orpc.chat.sendMessage.mutationOptions({
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        // Invalidate chat.get so loadedSkillIds is refreshed after a slash-invoke.
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: variables.id } }).queryKey,
        });
      },
    }),
  );

  // pendingTurnId bridges the gap between mutation response and SSE delivery.
  // Set from sendMessage response OR from router state (welcome screen flow).
  // Cleared when the turn completes in the SSE stream.
  const [pendingTurnId, setPendingTurnId] = useState<string | null>(routerTurnId);

  // Turn-scoped SSE URL: connect when we know a turn ID to follow.
  // Sources: pendingTurnId (just sent) or runtime.activeTurn (page reload mid-turn).
  const knownTurnId = pendingTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null;
  const streamUrl =
    knownTurnId && activeSessionId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${activeSessionId}/turns/${knownTurnId}/events`
      : null;

  const sseOptions = useMemo(
    () => ({
      onEvent: (eventType: string, data: Record<string, unknown>) => {
        if (eventType === 'turn_aborted') {
          const userMessage = typeof data['userMessage'] === 'string' ? data['userMessage'] : '';
          if (userMessage) {
            setPrefillValue(userMessage);
            setPrefillNonce((n) => n + 1);
          }
          setPendingTurnId(null);
        }
      },
    }),
    [],
  );

  const {
    messages: sseMessages,
    error: sseError,
    clearMessages,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    forceTurnAborted,
  } = useSSE(streamUrl, sseOptions);

  // stopTurnMutation optimistically unlocks the composer on stop click (resolves R1 + R4).
  // onMutate clears pendingTurnId and restores prefill immediately; onError rolls back.
  // The SSE turn_aborted handler becomes pure cleanup via the idempotent reducer.
  interface StopTurnVars {
    id: string;
    turnId: string;
  }
  interface StopTurnCtx {
    snapshot: { pendingTurnId: string | null; prefillValue: string | undefined; prefillNonce: number };
  }
  const stopTurnMutation = useMutation<{ ok: boolean }, Error, StopTurnVars, StopTurnCtx>({
    mutationFn: (input) => orpc.chat.stopTurn.call(input),
    onMutate: (vars) => {
      const { turnId } = vars;
      const snapshot = { pendingTurnId, prefillValue, prefillNonce };
      const userMessage = findUserMessageInTurn(sseMessages, turnId);
      forceTurnAborted(turnId, userMessage ?? '');
      if (userMessage) {
        setPrefillValue(userMessage);
        setPrefillNonce((n) => n + 1);
      }
      setPendingTurnId(null);
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshot) {
        setPendingTurnId(ctx.snapshot.pendingTurnId);
        setPrefillValue(ctx.snapshot.prefillValue);
        setPrefillNonce(ctx.snapshot.prefillNonce);
      }
      toast.error(extractErrorMessage(err));
    },
  });

  // Derive active-turn state from SSE messages via turn-slicing.
  const slices = useMemo(() => sliceIntoTurns(sseMessages), [sseMessages]);
  const activeSlice = slices.find((s) => s.status === 'active') ?? null;
  // pendingTurnId bridges the gap between mutation response and SSE delivery —
  // keeps the composer disabled until the stream connects and turn_start arrives.
  const active = activeSlice !== null || sendMessageMutation.isPending || pendingTurnId !== null;

  // Clear pendingTurnId once the matching turn completes (or errors) in SSE.
  useEffect(() => {
    if (!pendingTurnId) return;
    const match = slices.find((s) => s.turnId === pendingTurnId);
    if (match && match.status !== 'active') {
      setPendingTurnId(null);
    }
  }, [slices, pendingTurnId]);

  // Safety net: clear pendingTurnId if the SSE connection is permanently lost.
  // Without this, the UI stays locked after MAX_RETRIES with no way to recover.
  // Guard: only act when streamUrl is null (no new connection was established).
  // When a new turn sets pendingTurnId, streamUrl changes — if streamUrl is
  // non-null, the error is stale from a previous turn and will be cleared by
  // useSSE's own effect when the new connection opens.
  useEffect(() => {
    if (pendingTurnId && sseError?.startsWith('Connection lost')) {
      setPendingTurnId(null);
    }
  }, [pendingTurnId, streamUrl, sseError]);

  // Initialize SSE messages from historical data (once per session).
  // Race-safe: if the SSE stream has already delivered events for the active
  // turn before chat.get returns, we prepend the historical (pre-active-turn)
  // messages instead of replacing everything.
  const initializedSessionRef = useRef<string | null>(null);
  const sseMessagesRef = useRef(sseMessages);
  useEffect(() => {
    sseMessagesRef.current = sseMessages;
  });

  useEffect(() => {
    if (sessionData?.sseMessages && sessionId && initializedSessionRef.current !== sessionId) {
      initializedSessionRef.current = sessionId;
      // Filter out events belonging to the active turn — the SSE stream is the
      // single source of truth for that turn's data, not chat.get.
      const filtered = knownTurnId ? filterOutTurn(sessionData.sseMessages, knownTurnId) : sessionData.sseMessages;
      const currentSSE = sseMessagesRef.current;

      if (currentSSE.length > 0) {
        // SSE has already delivered events (race: SSE connected before chat.get
        // returned). Prepend historical messages instead of replacing.
        initializeMessages([...filtered, ...currentSSE]);
      } else {
        initializeMessages(filtered);
      }
    }
  }, [sessionData?.sseMessages, sessionId, knownTurnId, initializeMessages]);

  // Restore aborted prompt on initial load — if the tail of the session is a
  // turn_aborted and the user reloads, put the prompt back into the composer.
  const prefillRestoredRef = useRef<string | null>(null);
  useEffect(() => {
    const prompt = sessionData?.runtime?.lastAbortedPrompt;
    if (prompt && prefillRestoredRef.current !== sessionId) {
      prefillRestoredRef.current = sessionId ?? null;
      setPrefillValue(prompt);
      setPrefillNonce((n) => n + 1);
    }
  }, [sessionData?.runtime?.lastAbortedPrompt, sessionId]);

  // Clear messages when switching sessions.
  // When the welcome→first-message flow navigates to /chat/:id, the component
  // is REUSED (not remounted) because both the index and chat/:id routes render
  // <Chat />. useState(routerTurnId) ignores the initial value on reuse, so we
  // must explicitly adopt routerTurnId here — otherwise the turn ID from router
  // state is lost and no SSE stream opens (AI-562).
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (sessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = sessionId;
      clearMessages();
      setPendingTurnId(routerTurnId);
      initializedSessionRef.current = null;
      setPrefillValue(undefined);
      setPrefillNonce(0);
    }
  }, [sessionId, routerTurnId, clearMessages]);

  // React to change_title ui_messages — invalidate session queries to refresh title
  const lastTitleChangeRef = useRef<string | null>(null);
  useEffect(() => {
    let titleMsg: SSEMessage | undefined;
    for (let i = sseMessages.length - 1; i >= 0; i--) {
      const m = sseMessages[i]!;
      if (m.type === 'ui_message' && m.uiType === 'change_title') {
        titleMsg = m;
        break;
      }
    }
    if (titleMsg?.type === 'ui_message' && titleMsg.content !== lastTitleChangeRef.current) {
      lastTitleChangeRef.current = titleMsg.content;
      void queryClient.invalidateQueries({
        queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
      });
      if (sessionId) {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: sessionId } }).queryKey,
        });
      }
    }
  }, [sseMessages, queryClient, sessionId, orpc.chat.get, orpc.chat.list]);

  const isWelcome = !sessionId;
  usePageTitle(isWelcome ? 'New Chat' : sessionData?.session?.title);

  useEffect(() => {
    setSelectedCategory(null);
  }, [sessionId]);

  // Best-effort orphan cleanup: when the welcome→first-message flow creates a
  // session but sendMessage fails, we end up with an empty session row. The
  // user gets a toast on the welcome screen, so an uncleaned orphan would
  // appear in the sidebar with no obvious origin. Distinct from deleteMutation
  // (which navigates away on success) — we just want a silent best-effort
  // cleanup that refreshes the sidebar list.
  const cleanupOrphanMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const deleteMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        void navigate(wp('/chat'));
      },
    }),
  );

  const handleDelete = useCallback(() => {
    if (!sessionId) return;
    deleteMutation.mutate({ id: sessionId });
  }, [sessionId, deleteMutation]);

  const visibilityMutation = useMutation(
    orpc.chat.updateVisibility.mutationOptions({
      onSuccess: () => {
        if (!sessionId) return;
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: sessionId } }).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err: { message?: string }) => {
        toast.error(err.message ?? 'Failed to update chat visibility');
      },
    }),
  );

  const handleUpdateVisibility = useCallback(
    (visibility: 'private' | 'workspace') => {
      if (!sessionId) return;
      visibilityMutation.mutate({ id: sessionId, visibility });
    },
    [sessionId, visibilityMutation],
  );

  const sendMessage = useCallback(
    async (targetSessionId: string, content: string, invokedSkillIds?: string[]): Promise<void> => {
      // Add optimistic user_message — will be deduped when the SSE stream
      // delivers the persisted copy with a seq number.
      addMessage({ type: 'user_message', content });
      try {
        const result = await sendMessageMutation.mutateAsync({
          id: targetSessionId,
          content,
          invokedSkillIds: invokedSkillIds ?? [],
        });
        // Store the turnId so the SSE URL updates and the stream connects.
        if (result.turnId) {
          setPendingTurnId(result.turnId);
        }
      } catch (err: unknown) {
        // TURN_IN_PROGRESS: another turn is already running (post-stop race).
        // Remove the optimistic bubble and restore the prompt so the user can
        // retry once the abort completes. The stream URL picks up the active
        // turn from sessionData after refetch.
        const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
        if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
          removeLastUserMessage(content);
          setPrefillValue(content);
          setPrefillNonce((n) => n + 1);
          setPendingTurnId(errData.activeTurnId);
          return;
        }
        // For other errors, remove the optimistic message and show the error.
        removeLastUserMessage(content);
        addMessage({ type: 'error', message: extractErrorMessage(err) });
      }
    },
    [sendMessageMutation, addMessage, removeLastUserMessage],
  );

  const handleSend = useCallback(
    async (content: string, invokedSkillIds?: string[]): Promise<void> => {
      if (!sessionId) {
        // Welcome → first message: persist the user_message BEFORE navigating,
        // so the new <Chat /> instance's chat.get returns the row instead of
        // racing sendMessage and stamping an empty sseMessages into local
        // state. sendMessage is a single DB write — the backend fires the
        // agent turn without awaiting it.
        //
        // The welcome JSX has no MessageList (and no ThinkingIndicator), so
        // errors surface via toast and we don't bother flipping agentActive
        // until the new chat instance mounts and derives it from SSE state.
        setSending(true);
        try {
          const data = await createMutation.mutateAsync({});
          let turnId: string | undefined;
          try {
            const result = await sendMessageMutation.mutateAsync({
              id: data.session.id,
              content,
              invokedSkillIds: invokedSkillIds ?? [],
            });
            turnId = result.turnId;
          } catch (err: unknown) {
            cleanupOrphanMutation.mutate({ id: data.session.id });
            toast.error(extractErrorMessage(err));
            return;
          }
          // Pass turnId via router state so the new Chat instance can open the
          // SSE stream immediately and filter the turn from chat.get data.
          void navigate(wp(`/chat/${data.session.id}`), {
            state: turnId ? { pendingTurnId: turnId } : undefined,
          });
        } catch (err: unknown) {
          toast.error(extractErrorMessage(err));
        } finally {
          setSending(false);
        }
        return;
      }
      await sendMessage(sessionId, content, invokedSkillIds);
    },
    [sessionId, navigate, wp, createMutation, sendMessage, sendMessageMutation, cleanupOrphanMutation],
  );

  // Loading state
  if (settingsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3" role="status" aria-label="Loading">
          <Shield size={28} className="animate-pulse text-brand" aria-hidden="true" />
          <LoaderCircle size={18} className="animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (hasRudderstack === false) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl rounded-[1.75rem] border border-amber-200/80 bg-amber-50/72 p-8 text-center shadow-[0_24px_60px_-42px_rgba(147,98,24,0.35)]">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl bg-white/90 text-amber-600 shadow-[0_18px_36px_-30px_rgba(147,98,24,0.4)]">
            <Shield className="size-5" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Workspace setup required</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Rudder AI cannot start a new conversation until RudderStack is connected for this workspace. You can still
            open the messages view while setup is being completed.
          </p>
          <div className="mt-6 flex justify-center">
            <Link
              to={wp('/chats')}
              className="inline-flex items-center rounded-full border border-amber-300/90 bg-white/90 px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-white"
            >
              Open messages
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Content: Welcome or Messages */}
      {isWelcome ? (
        <div
          className="flex flex-1 flex-col items-center px-4 pt-10 pb-10 md:pt-14"
          role="presentation"
          onClick={() => selectedCategory && setSelectedCategory(null)}
        >
          <div className="w-full max-w-4xl animate-scale-in">
            <div className="mb-8 text-center">
              <h1 className="mx-auto max-w-3xl text-[clamp(1.8rem,3.6vw,2.8rem)] font-bold tracking-[-0.04em] text-foreground">
                What do you wish to do?
              </h1>
              <p className="mx-auto mt-3 max-w-2xl text-[0.9375rem] leading-relaxed text-muted-foreground">
                Ask about your data pipeline, build dashboards, or fix data quality issues.
              </p>
            </div>

            <div className="rounded-[2rem] border border-border/70 bg-white/58 p-4 shadow-[0_28px_70px_-52px_rgba(47,66,112,0.3)] backdrop-blur-sm md:p-5">
              <MessageInput
                onSend={(c, ids) => void handleSend(c, ids)}
                disabled={sending || active}
                variant="hero"
                workspaceSlug={slug}
                hasPromotedSkills={hasPromotedSkills}
                prefillValue={prefillValue}
                prefillNonce={prefillNonce}
              />

              <div className="mt-5 flex flex-wrap justify-center gap-3">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.label}
                    onClick={() => setSelectedCategory(cat.label)}
                    className={cn(
                      'flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-all',
                      selectedCategory === cat.label
                        ? 'border-primary/20 bg-primary/10 text-primary shadow-[0_18px_36px_-30px_rgba(16,94,213,0.5)]'
                        : 'border-border/80 bg-white/82 text-muted-foreground hover:border-primary/20 hover:bg-white hover:text-foreground',
                    )}
                  >
                    <cat.icon size={14} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedCategory
              ? (() => {
                  const cat = CATEGORIES.find((c) => c.label === selectedCategory);
                  if (!cat) return null;
                  return (
                    <div
                      className="mx-auto mt-4 w-full max-w-3xl animate-slide-up rounded-[1.5rem] border border-border/75 bg-white/88 shadow-[0_22px_54px_-38px_rgba(47,66,112,0.28)] backdrop-blur-sm"
                      role="presentation"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-5 py-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <cat.icon size={14} />
                          {cat.label}
                        </div>
                        <button
                          onClick={() => setSelectedCategory(null)}
                          aria-label="Close suggestions"
                          className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      {cat.examples.map((ex, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setSelectedCategory(null);
                            void handleSend(ex.prompt);
                          }}
                          className="w-full border-t border-border/50 px-5 py-4 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                        >
                          {ex.label}
                        </button>
                      ))}
                    </div>
                  );
                })()
              : null}
          </div>
        </div>
      ) : (
        <>
          {/* Chat header */}
          {sessionData?.session && (
            <div className="flex items-center justify-end gap-2 border-b border-border/30 px-4 py-2">
              <ChatSharePopover
                visibility={
                  sessionData.session.visibility === 'workspace' || sessionData.session.visibility === 'private'
                    ? sessionData.session.visibility
                    : 'private'
                }
                onUpdateVisibility={handleUpdateVisibility}
                pending={visibilityMutation.isPending}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2">
                    <EllipsisVertical className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={deleteMutation.isPending}
                    onClick={handleDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 size-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          <MessageList
            messages={sseMessages}
            thinking={active}
            onAction={(content) => void handleSend(content)}
            actionsDisabled={active}
          />
        </>
      )}

      {/* Input — only show bottom bar for active chats */}
      {!isWelcome && (
        <MessageInput
          onSend={(c, ids) => void handleSend(c, ids)}
          disabled={sending || active}
          variant="chat"
          thinkingText={active ? 'Rudder AI is thinking...' : undefined}
          loadedSkills={(sessionData?.session?.loadedSkillIds ?? []).map((id) => ({
            id,
            title: (contextHubData?.items ?? []).find((s) => s.id === id)?.title ?? id,
          }))}
          workspaceSlug={slug}
          hasPromotedSkills={hasPromotedSkills}
          onStop={
            knownTurnId && activeSessionId
              ? () => stopTurnMutation.mutate({ id: activeSessionId, turnId: knownTurnId })
              : undefined
          }
          prefillValue={prefillValue}
          prefillNonce={prefillNonce}
        />
      )}
    </div>
  );
}
